import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Alert, Box, Button, Card, CardContent, Chip, Grid, Stack, Typography,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import { apiConstants, get } from '../../common/constants'
import Loader from '../../common/Loader'
import PageTitle from '../../components/PageTitle'
import { formatDate } from '../../common/util'
import CopyIcon from '../../common/CopyIcon'

const fmtKg = (v, dp = 3) =>
  v == null || isNaN(v) ? '—' : `${Number(v).toFixed(dp)} kg`

// ── 4-stat hero card ────────────────────────────────────────────────────────
// Avg Exact Match | Prozo Pushed | Per-SKU Fallback | Difference
// Difference compares the per-SKU fallback to the chosen exact-match value,
// surfacing the over-prediction the exact match avoided.
function HeroStats({ avgExactMatch, prozoPushed, fallbackWeight, difference, matchCount, packagingPct }) {
  const pctLabel =
    packagingPct != null ? `${(Number(packagingPct) * 100).toFixed(0)}%` : '25%'
  return (
    <Card
      sx={{
        mb: 2,
        bgcolor: '#ecfdf5',
        border: '1px solid #d1fae5',
      }}
    >
      <CardContent sx={{ py: 2.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} textAlign='center'>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#16a34a', mb: 0.5 }}>
              Avg Exact Match Weight
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#065f46' }}>
              {fmtKg(avgExactMatch)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#22c55e', mt: 0.5 }}>
              Based on {matchCount} identical shipment{matchCount === 1 ? '' : 's'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3} textAlign='center'>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6366f1', mb: 0.5 }}>
              Predicted Weight
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#4338ca' }}>
              {fmtKg(prozoPushed)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#818cf8', mt: 0.5 }}>
              Final weight pushed for allocation
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3} textAlign='center'>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#a16207', mb: 0.5 }}>
              Per-SKU Fallback
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#92400e' }}>
              {fmtKg(fallbackWeight)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#d97706', mt: 0.5 }}>
              Sum of SKU avg + {pctLabel} packaging adj.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3} textAlign='center'>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280', mb: 0.5 }}>
              Difference
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#111' }}>
              {fmtKg(difference)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5 }}>
              Saved by using exact match over fallback
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

// ── Pill row of the SKUs in the current shipment ───────────────────────────
function CurrentSkuCombination({ products = [] }) {
  if (!products.length) return null
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.25 }}>
          <Inventory2OutlinedIcon sx={{ fontSize: 18, color: '#6366f1' }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
            Current Shipment SKU Combination
          </Typography>
        </Stack>
        <Stack direction='row' spacing={1.25} flexWrap='wrap' rowGap={1.25}>
          {products.map((p, i) => (
            <Box
              key={`${p.sku}-${i}`}
              sx={{
                bgcolor: '#eef2ff',
                border: '1px solid #c7d2fe',
                borderRadius: 1,
                px: 1.5,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography sx={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 700, color: '#4338ca' }}>
                {p.sku || '—'}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#374151' }}>
                {p.skuName || ''}
              </Typography>
              <Chip
                size='small'
                label={`×${p.quantity ?? 1}`}
                sx={{ height: 20, fontSize: 10.5, bgcolor: '#c7d2fe', color: '#3730a3' }}
              />
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}

// ── Historical shipments table (with Average summary row) ──────────────────
function HistoricalTable({ rows, products, avgWeight }) {
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.25 }}>
          <HistoryOutlinedIcon sx={{ fontSize: 18, color: '#16a34a' }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
            Historical Shipments with Exact Same SKU Combo ({rows.length})
          </Typography>
        </Stack>
        <Table size='small' sx={{ '& td, & th': { fontSize: 12, py: 0.75 } }}>
          <TableHead>
            <TableRow sx={{ '& th': { color: '#6b7280', fontWeight: 700, bgcolor: '#f0fdf4', textTransform: 'uppercase', letterSpacing: 0.3 } }}>
              <TableCell sx={{ width: 36 }}>#</TableCell>
              <TableCell>AWB</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Courier</TableCell>
              <TableCell>SKUs</TableCell>
              <TableCell align='right'>Transit Charged Weight</TableCell>
              <TableCell align='right'>Transit Charged Slab</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((s, i) => (
              <TableRow key={`${s.awb}-${i}`} hover sx={{ '&:hover': { bgcolor: '#f0fdf4' } }}>
                <TableCell sx={{ color: '#9ca3af' }}>{i + 1}</TableCell>
                <TableCell sx={{ color: '#3b82f6', fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {s.awb || '—'}
                    <CopyIcon text={s.awb} />
                  </Box>
                </TableCell>
                <TableCell>
                  {s.shipmentDate ? formatDate(s.shipmentDate, { format: 'yyyy-MM-dd' }) : '—'}
                </TableCell>
                <TableCell>
                  <Stack direction='row' spacing={0.5} alignItems='center'>
                    <LocalShippingOutlinedIcon sx={{ fontSize: 14, color: '#6b7280' }} />
                    <Typography sx={{ fontSize: 12 }}>{s.courierPartnerName || '—'}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction='row' spacing={0.5} flexWrap='wrap' rowGap={0.5}>
                    {products.map((p, pi) => (
                      <Chip
                        key={`${p.sku}-${pi}`}
                        size='small'
                        label={`${p.sku} ×${p.quantity ?? 1}`}
                        sx={{ height: 20, fontSize: 10.5, bgcolor: '#dcfce7', color: '#15803d', fontWeight: 600 }}
                      />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align='right'>{fmtKg(s.courierActualWeight)}</TableCell>
                <TableCell align='right'>
                  <Chip
                    size='small'
                    label={fmtKg(s.courierActualWeightSlab)}
                    sx={{ height: 20, fontSize: 10.5, bgcolor: '#fef3c7', color: '#92400e' }}
                  />
                </TableCell>
              </TableRow>
            ))}
            {rows.length > 0 && (
              <TableRow sx={{ bgcolor: '#dcfce7', borderTop: '2px solid #86efac' }}>
                <TableCell colSpan={5} sx={{ fontWeight: 700, color: '#065f46', fontSize: 13 }}>
                  Average (Exact Match Weight)
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: 800, color: '#065f46', fontSize: 14 }}>
                  {fmtKg(avgWeight)}
                </TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

// ── Footer educational card ─────────────────────────────────────────────────
function WhyExactMatch() {
  return (
    <Card sx={{ bgcolor: '#ecfdf5', border: '1px solid #d1fae5' }}>
      <CardContent>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#065f46', mb: 0.75 }}>
          Why Exact Match Takes Preference
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#047857', lineHeight: 1.6 }}>
          When we find historical shipments with the exact same combination of SKUs and quantities,
          their combined actual weight is a more accurate predictor than summing individual SKU averages.
          Individual averages require a packaging adjustment factor because each SKU was historically
          shipped with its own packing material, but when shipped together, a single package is used.
          Exact matches inherently account for this.
        </Typography>
      </CardContent>
    </Card>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function ExactMatchShipmentsPage() {
  const { awbNumber } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [row, setRow] = useState(location.state?.row || null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (row && row.awbNumber === awbNumber) return
    setLoading(true)
    get(`${apiConstants.WEIGHT_LEDGER_BY_AWB}/${awbNumber}`)
      .then((res) => setRow(res?.data || null))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awbNumber])

  // Hooks must run unconditionally — derive data with optional chaining.
  const calc = row?.prozoCalc
  const products = row?.products || []
  const isMultiSku = products.length > 1
  const skuCalcDetails = useMemo(() => calc?.skuCalcDetails || [], [calc])
  // Multi-SKU exact-match shipments live on prozoCalc.comboHistory.
  // Single-SKU exact match: skuCalcDetails[0].last3Shipments when calcStatus says EXACT.
  const exactMatchShipments = useMemo(() => {
    if (!calc) return []
    if (isMultiSku) return calc.comboHistory || []
    const s = String(calc.calcStatus || '').toUpperCase()
    if (s.includes('EXACT')) return skuCalcDetails[0]?.last3Shipments || []
    return []
  }, [calc, isMultiSku, skuCalcDetails])

  const avgExactMatch = useMemo(() => {
    if (!exactMatchShipments.length) return 0
    const total = exactMatchShipments.reduce(
      (s, r) => s + (Number(r.courierActualWeight) || 0),
      0,
    )
    return +(total / exactMatchShipments.length).toFixed(3)
  }, [exactMatchShipments])

  if (loading) {
    return (
      <>
        <PageTitle>Exact Match Shipments</PageTitle>
        <Loader />
      </>
    )
  }

  if (!row) {
    return (
      <Box sx={{ p: 3 }}>
        <PageTitle>Exact Match Shipments</PageTitle>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Alert severity='warning' sx={{ mt: 2 }}>No record found for AWB {awbNumber}.</Alert>
      </Box>
    )
  }

  if (!exactMatchShipments.length) {
    return (
      <Box>
        <PageTitle>Exact Match Shipments</PageTitle>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/weight/ledger/calc/${awbNumber}`)}
          sx={{ mb: 2, color: '#6366f1', fontWeight: 600 }}
        >
          Back to Calculation
        </Button>
        <Alert severity='info'>
          No exact-match historical shipments are available for this AWB.
        </Alert>
      </Box>
    )
  }

  const prozoPushed = row?.weights?.prozoAllocated
  const fallbackWeight = calc?.individualFallbackWeight
  // Difference = fallback − chosen exact-match. Surfaces what the exact
  // match preference saved over the per-SKU fallback path.
  const difference =
    fallbackWeight != null && avgExactMatch != null
      ? +(fallbackWeight - avgExactMatch).toFixed(3)
      : null

  return (
    <Box>
      <PageTitle>Exact Match Shipments</PageTitle>
      {/* Sticky-feeling header strip */}
      <Card sx={{ mb: 1.5 }}>
        <CardContent sx={{ py: 1.75 }}>
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: 1.25,
                bgcolor: '#16a34a',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <LayersOutlinedIcon sx={{ color: '#fff' }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800 }}>Exact Match Shipments</Typography>
              <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                AWB:{' '}
                <Box component='span' sx={{ color: '#6366f1', fontWeight: 600, fontFamily: 'monospace' }}>
                  {row.awbNumber}
                </Box>
                {' · '}
                Same SKU combination found in {exactMatchShipments.length} historical
                shipment{exactMatchShipments.length === 1 ? '' : 's'}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/weight/ledger/calc/${awbNumber}`, { state: { row } })}
        sx={{ mb: 1.5, color: '#6366f1', fontWeight: 600 }}
      >
        Back to Calculation
      </Button>

      <CurrentSkuCombination products={products} />

      <HeroStats
        avgExactMatch={avgExactMatch}
        prozoPushed={prozoPushed}
        fallbackWeight={fallbackWeight}
        difference={difference}
        matchCount={exactMatchShipments.length}
        packagingPct={calc?.packagingAdjPct}
      />

      <HistoricalTable
        rows={exactMatchShipments}
        products={products}
        avgWeight={avgExactMatch}
      />

      <WhyExactMatch />
    </Box>
  )
}
